import { useEffect, useState, useMemo } from "react";
import MainLayout from "@/layouts/MainLayout";
import { Card } from "@/components/ui/card";
import { ExternalLink, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { getResources, Resource } from "@/lib/contentful";
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

// Resource card component
const ResourceCard = ({ resource }: { resource: Resource }) => {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <a 
          href={resource.link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block h-full"
        >
          <Card className="h-full p-6 transition-all duration-300 hover:shadow-lg hover:border-primary">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                <ExternalLink className="h-5 w-5" />
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{resource.title}</h3>
            <div className="text-muted-foreground">
              {documentToReactComponents(resource.description)}
            </div>
            {resource.tags && resource.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {resource.tags.map(tag => (
                  <Badge 
                    key={tag} 
                    variant="secondary"
                    className="text-xs"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </Card>
        </a>
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{resource.title}</h4>
          <div className="text-sm text-muted-foreground">
            {documentToReactComponents(resource.description)}
          </div>
          <p className="text-xs text-muted-foreground">Click to visit resource →</p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const ResourcesPage = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const resourcesData = await getResources();
        setResources(resourcesData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Get all unique tags from resources
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    resources.forEach(resource => {
      resource.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [resources]);

  // Filter resources based on search query and selected tags
  const filteredResources = useMemo(() => {
    return resources.filter(resource => {
      const matchesSearch = searchQuery === "" || 
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        JSON.stringify(resource.description).toLowerCase().includes(searchQuery.toLowerCase());

      const matchesTags = selectedTags.length === 0 ||
        selectedTags.every(tag => resource.tags?.includes(tag));

      return matchesSearch && matchesTags;
    });
  }, [resources, searchQuery, selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <MainLayout
      title="AI Resources | Niumora"
      description="Access our curated collection of the best AI resources to help you learn, implement, and innovate with artificial intelligence in your business."
      keywords="AI resources, artificial intelligence guides, AI tools, AI tutorials, AI for business, Niumora resources"
      ogType="website"
    >
      <div className="py-24 container max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold font-display tracking-tight mb-4">
            AI <span className="gradient-text">Resources</span>
          </h1>
          <p className="text-lg text-muted-foreground">
            Curated collection of the best AI resources to help you learn, build, and innovate with artificial intelligence. For downloadable resources, visit our <a href="/downloads" className="text-primary hover:underline">Downloads</a> page.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Tags Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {allTags.map(tag => (
              <Badge
                key={tag}
                variant={selectedTags.includes(tag) ? "default" : "secondary"}
                className="cursor-pointer transition-colors"
                onClick={() => toggleTag(tag)}
              >
                {tag}
                {selectedTags.includes(tag) && (
                  <X className="ml-1 h-3 w-3 inline-block" />
                )}
              </Badge>
            ))}
          </div>

          {/* Results count */}
          <p className="text-center text-sm text-muted-foreground">
            Showing {filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'}
            {(searchQuery || selectedTags.length > 0) && ' matching your filters'}
          </p>
        </div>

        {/* Resources Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => (
              <ResourceCard key={index} resource={resource} />
            ))}
            {filteredResources.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <p>No resources found matching your criteria.</p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedTags([]);
                  }}
                  className="text-primary hover:underline mt-2"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ResourcesPage;
